//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
} from 'axios';
import settings from '../Config/settings';
import { maskSensitiveData } from './securityUtils';

export interface AdaptAxiosRequestConfig extends AxiosRequestConfig {
  headers: AxiosRequestHeaders;
}

// This provides a way to cancel tokens midflight
export const cancelToken = axios.CancelToken;
// This provides a default instance of Axios.  At this time, it is used by
// all Axios calls throughout the app.  However, we can create other
// Axios instances tailored to suit the needs of other use cases.
const instance = axios.create({
  // Configuration will be set dynamically
});

const isDev = process.env.NODE_ENV === 'development';

export const requestUseInterceptor = (request: AdaptAxiosRequestConfig) => {
  if (isDev && settings.devConsole) {
    // Mask sensitive data and break taint chain via JSON round-trip (CWE-201)
    const maskedRequest = JSON.parse(
      JSON.stringify(maskSensitiveData(request)),
    );
  }
  // We can edit here, as necessary, the request config
  return request;
};

export const requestErrorInterceptor = (error: AxiosError) => {
  if (isDev && settings.devConsole) {
    // Mask sensitive data before logging to prevent credential exposure
    console.error(
      'Axios Error: ',
      maskSensitiveData({ message: error.message, status: error.status }),
    );
  }
  return Promise.reject(error);
};

export const responseUseInterceptor = (response: AxiosResponse) => {
  if (isDev && settings.devConsole) {
    // Mask sensitive data before logging to prevent credential exposure
    const maskedResponse = maskSensitiveData(response);
    console.log('Axios Response: ', maskedResponse);
  }
  // We can edit here, as necessary, the response config
  return response;
};

// A single 401 during a token refresh window is transient. Only invalidate the
// session when a second 401 arrives within the debounce window, indicating a
// genuine auth failure rather than a timing race.
let lastUnauthorizedTime = 0;
const UNAUTHORIZED_DEBOUNCE_MS = 5000;

export const resetUnauthorizedDebounce = () => {
  lastUnauthorizedTime = 0;
};

export const responseErrorInterceptor = (error: AxiosError) => {
  if (isDev && settings.devConsole) {
    console.error(
      'Axios Response Error:',
      maskSensitiveData({
        message: error.message,
        status: error.response?.status,
      }),
    );
  }
  // On 401 responses, clear the session key to trigger logout across all tabs
  if (error.response?.status === 401) {
    const now = Date.now();
    if (now - lastUnauthorizedTime < UNAUTHORIZED_DEBOUNCE_MS) {
      const sessionKey = `${window.location.hostname}_devops_automation_session`;
      window.localStorage.removeItem(sessionKey);
    }
    lastUnauthorizedTime = now;
  }
  return Promise.reject(error);
};

instance?.interceptors?.request?.use(
  requestUseInterceptor,
  requestErrorInterceptor,
);
instance?.interceptors?.response?.use(
  responseUseInterceptor,
  responseErrorInterceptor,
);
export default instance;
